import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Option, Select, Sheet,
    Stack, Tab, TabList, TabPanel, Tabs, Typography, tabClasses,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

import { useAdmin } from '../../../AdminContext';
import {
    APIAdminAdsReportAdvertisers, APIAdminAdsReportModeration,
    APIAdminAdsReportOverview, APIAdminAdsReportPlacements,
    APIAdminAdsReportExport, APIAdminAdsRunAggregation, adminAdsError,
} from '../../../api_admin_ads';
import AdBreakdownTable from '../../../components/Ads/reports/AdBreakdownTable';
import AdMetricCard from '../../../components/Ads/reports/AdMetricCard';
import { REJECTION_REASONS, formatInt, formatMoney } from '../../../components/Ads/adsConstants';
import { saveBlob } from '../../../api_ads';


const PERIODS = [
    { value: '7d', label: '7 дней' },
    { value: '30d', label: '30 дней' },
    { value: '90d', label: '90 дней' },
];

const money = (value) => `${formatMoney(value)} ₽`;
const percent = (value) => `${Number(value || 0).toFixed(2)}%`;
const reasonLabel = (code) =>
    REJECTION_REASONS.find((r) => r.value === code)?.label || code;


export default function AdminAdsReportsPage() {
    const navigate = useNavigate();
    const { adminToken: token, has } = useAdmin();
    const canManage = has('ads.manage');

    const [period, setPeriod] = useState('30d');
    const [overview, setOverview] = useState(null);
    const [advertisers, setAdvertisers] = useState([]);
    const [placements, setPlacements] = useState([]);
    const [moderation, setModeration] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState(0);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            setError(null);
            const [o, a, p, m] = await Promise.all([
                APIAdminAdsReportOverview(token, { period }),
                APIAdminAdsReportAdvertisers(token, { period, limit: 50 }),
                APIAdminAdsReportPlacements(token, { period }),
                APIAdminAdsReportModeration(token, { period }),
            ]);
            setOverview(o);
            setAdvertisers(a?.items || []);
            setPlacements(p?.items || []);
            setModeration(m || null);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось загрузить отчёты'));
        } finally {
            setLoading(false);
        }
    }, [token, period]);

    useEffect(() => { load(); }, [load]);

    const download = async (report) => {
        try {
            setBusy(`export-${report}`);
            const blob = await APIAdminAdsReportExport(token, report, { period });
            saveBlob(blob, `ads_admin_${report}_${period}.csv`);
        } catch (e) {
            setError('Не удалось выгрузить отчёт');
        } finally {
            setBusy(null);
        }
    };

    const runAggregation = async () => {
        setBusy('aggregate');
        setError(null);
        try {
            const res = await APIAdminAdsRunAggregation(token);
            setNotice(`Агрегация выполнена: показов ${res?.stats?.impressions ?? 0}, `
                + `кликов ${res?.stats?.clicks ?? 0}`);
            await load();
        } catch (e) {
            setError(adminAdsError(e));
        } finally {
            setBusy(null);
        }
    };

    const stats = overview?.overview || {};

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography level="h4">Отчёты по рекламе</Typography>
                    <Typography level="body-sm" color="neutral">
                        Агрегаты обновляются каждые 10 минут, полный пересчёт за вчера — ночью
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Select size="sm" value={period} onChange={(_, v) => v && setPeriod(v)}
                            sx={{ minWidth: 130 }}>
                        {PERIODS.map((p) => (
                            <Option key={p.value} value={p.value}>{p.label}</Option>
                        ))}
                    </Select>
                    {canManage && (
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<SyncRoundedIcon />}
                            loading={busy === 'aggregate'}
                            onClick={runAggregation}
                        >
                            Пересчитать
                        </Button>
                    )}
                </Stack>
            </Stack>

            {notice && (
                <Alert color="success" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={() => setNotice(null)}>
                        Скрыть
                    </Button>
                }>{notice}</Alert>
            )}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && !overview ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <AdMetricCard size="lg" label="Доход от рекламы" value={money(stats.spend)} />
                        <AdMetricCard size="lg" label="Показы" value={formatInt(stats.impressions)} />
                        <AdMetricCard label="Клики" value={formatInt(stats.clicks)} />
                        <AdMetricCard label="CTR" value={percent(stats.ctr)} />
                        <AdMetricCard label="eCPM" value={money(stats.ecpm)} />
                    </Stack>

                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                        <AdMetricCard
                            label="Заполняемость"
                            value={percent(stats.fill_rate)}
                            hint={`запросов: ${formatInt(stats.requests)}`}
                        />
                        <AdMetricCard
                            label="Доля недействительных кликов"
                            value={percent(stats.fraud_rate)}
                            hint={`отфильтровано: ${formatInt(stats.clicks_suspicious)}`}
                        />
                        <AdMetricCard
                            label="Отклонено на модерации"
                            value={percent(moderation?.rejection_rate)}
                            hint={`проверено: ${formatInt(moderation?.moderated_total)}`}
                        />
                        <AdMetricCard
                            label="Среднее время проверки"
                            value={moderation?.avg_delay_minutes != null
                                ? `${Math.round(moderation.avg_delay_minutes)} мин`
                                : '—'}
                            hint={`сейчас в очереди: ${formatInt(moderation?.pending_now)}`}
                        />
                    </Stack>

                    {Number(stats.fill_rate) < 40 && Number(stats.requests) > 1000 && (
                        <Alert color="warning" sx={{ mb: 2 }}>
                            <Box>
                                <Typography level="title-sm">Низкая заполняемость</Typography>
                                <Typography level="body-sm">
                                    Больше половины запросов уходят без объявления — инвентарь
                                    простаивает. Проверьте минимальные ставки площадок
                                    и число активных кампаний.
                                </Typography>
                            </Box>
                        </Alert>
                    )}

                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                        <TabList sx={{
                            mb: 2, borderRadius: 'md',
                            [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                                bgcolor: 'primary.softBg', color: 'primary.700',
                            },
                        }}>
                            <Tab>Площадки</Tab>
                            <Tab>Рекламодатели</Tab>
                            <Tab>Модерация</Tab>
                        </TabList>

                        <TabPanel value={0} sx={{ p: 0 }}>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                                <Button
                                    size="sm" variant="outlined" color="neutral"
                                    startDecorator={<DownloadRoundedIcon />}
                                    loading={busy === 'export-placements'}
                                    onClick={() => download('placements')}
                                >
                                    Скачать CSV
                                </Button>
                            </Stack>
                            <AdBreakdownTable
                                defaultSort="spend"
                                rows={placements}
                                columns={[
                                    { key: 'name', label: 'Площадка', width: 220 },
                                    {
                                        key: 'requests', label: 'Запросов', align: 'right', width: 110,
                                        render: (r) => formatInt(r.requests),
                                    },
                                    {
                                        key: 'fill_rate', label: 'Заполняемость',
                                        align: 'right', width: 130,
                                        render: (r) => (
                                            <Chip
                                                size="sm"
                                                variant="soft"
                                                color={r.fill_rate >= 60 ? 'success'
                                                    : r.fill_rate >= 30 ? 'warning' : 'danger'}
                                            >
                                                {percent(r.fill_rate)}
                                            </Chip>
                                        ),
                                    },
                                    {
                                        key: 'impressions', label: 'Показы', align: 'right', width: 110,
                                        render: (r) => formatInt(r.impressions),
                                        total: (t) => formatInt(t.impressions),
                                    },
                                    {
                                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                                        render: (r) => percent(r.ctr),
                                        total: (t) => percent(t.ctr),
                                    },
                                    {
                                        key: 'ecpm', label: 'eCPM', align: 'right', width: 110,
                                        render: (r) => money(r.ecpm),
                                        total: (t) => money(t.ecpm),
                                    },
                                    {
                                        key: 'spend', label: 'Доход', align: 'right', width: 120,
                                        render: (r) => money(r.spend),
                                        total: (t) => money(t.spend),
                                    },
                                ]}
                            />
                        </TabPanel>

                        <TabPanel value={1} sx={{ p: 0 }}>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                                <Button
                                    size="sm" variant="outlined" color="neutral"
                                    startDecorator={<DownloadRoundedIcon />}
                                    loading={busy === 'export-advertisers'}
                                    onClick={() => download('advertisers')}
                                >
                                    Скачать CSV
                                </Button>
                            </Stack>
                            <AdBreakdownTable
                                defaultSort="spend"
                                rows={advertisers}
                                columns={[
                                    {
                                        key: 'title', label: 'Рекламодатель', width: 260,
                                        render: (r) => (
                                            <Box
                                                sx={{ cursor: 'pointer', color: 'primary.600' }}
                                                onClick={() => navigate('/admin/ads/accounts')}
                                            >
                                                {r.title || `Аккаунт #${r.account_id}`}
                                            </Box>
                                        ),
                                    },
                                    {
                                        key: 'impressions', label: 'Показы', align: 'right', width: 120,
                                        render: (r) => formatInt(r.impressions),
                                        total: (t) => formatInt(t.impressions),
                                    },
                                    {
                                        key: 'clicks', label: 'Клики', align: 'right', width: 110,
                                        render: (r) => formatInt(r.clicks),
                                        total: (t) => formatInt(t.clicks),
                                    },
                                    {
                                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                                        render: (r) => percent(r.ctr),
                                        total: (t) => percent(t.ctr),
                                    },
                                    {
                                        key: 'spend', label: 'Потрачено', align: 'right', width: 130,
                                        render: (r) => money(r.spend),
                                        total: (t) => money(t.spend),
                                    },
                                ]}
                            />
                        </TabPanel>

                        <TabPanel value={2} sx={{ p: 0 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', flex: 1 }}>
                                    <Typography level="title-sm" sx={{ mb: 1.5 }}>
                                        Причины отказов
                                    </Typography>
                                    {(moderation?.by_reason || []).length === 0 ? (
                                        <Typography level="body-sm" color="neutral">
                                            За период отказов не было
                                        </Typography>
                                    ) : (
                                        <Stack spacing={0.8}>
                                            {moderation.by_reason.map((row) => (
                                                <Stack
                                                    key={row.reason}
                                                    direction="row"
                                                    justifyContent="space-between"
                                                >
                                                    <Typography level="body-sm">
                                                        {reasonLabel(row.reason)}
                                                    </Typography>
                                                    <Chip size="sm" variant="soft">{row.count}</Chip>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    )}
                                </Sheet>

                                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', flex: 1 }}>
                                    <Typography level="title-sm" sx={{ mb: 1.5 }}>
                                        Активность модераторов
                                    </Typography>
                                    {(moderation?.by_admin || []).length === 0 ? (
                                        <Typography level="body-sm" color="neutral">
                                            Решений за период не было
                                        </Typography>
                                    ) : (
                                        <Stack spacing={0.8}>
                                            {moderation.by_admin.map((row) => (
                                                <Stack
                                                    key={row.admin_id}
                                                    direction="row"
                                                    justifyContent="space-between"
                                                >
                                                    <Typography level="body-sm">
                                                        {row.admin__full_name || `Админ #${row.admin_id}`}
                                                    </Typography>
                                                    <Chip size="sm" variant="soft">{row.count}</Chip>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    )}
                                </Sheet>
                            </Stack>
                        </TabPanel>
                    </Tabs>
                </>
            )}
        </Box>
    );
}